import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import Popover from '../../Popover/Popover';
import Search from '../../Search/Search';
import Button from '../../Button/Button';
import Icon from '../../Icon2/Icon';
import FieldLabel from '../../FieldLabel/FieldLabel';
import Tooltip from '../../Tooltip/Tooltip';
import './ReferencedEntries.css';

export interface ReferenceEntry {
  title: string;
  entry_uid: string;
  content_type_uid: string;
  content_type_title: string;
  locale: string;
}

export interface LocaleData {
  code: string;
  name: string;
}

export interface ReferencedEntriesProps {
  /**
   * Array of referenced entries
   */
  entries?: ReferenceEntry[];
  /**
   * Locale data for mapping locale codes to display names
   */
  localeData?: LocaleData[];
  /**
   * Callback when an entry is clicked
   */
  onEntryClick?: (entry: ReferenceEntry) => void;
  /**
   * Maximum number of entries to show before "View All" (default: 4)
   */
  maxVisibleEntries?: number;
  /**
   * Test ID for testing
   */
  testId?: string;
}

/**
 * Get locale display name from locale code
 */
const getLocaleName = (locale: string, localeData?: LocaleData[]): string => {
  if (!localeData || !locale) return locale;
  const localeItem = localeData.find((item) => item.code === locale);
  return localeItem?.name || locale;
};

/**
 * TruncatedText component - shows tooltip only when text is truncated
 */
interface TruncatedTextProps {
  text: string;
  className: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  role?: string;
  tabIndex?: number;
  testId?: string;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  className,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  testId,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation, text]);

  const textElement = (
    <div
      ref={textRef}
      className={className}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      data-test-id={testId}
    >
      {text}
    </div>
  );

  if (isTruncated) {
    return (
      <Tooltip content={text} position="top" type="secondary">
        {textElement}
      </Tooltip>
    );
  }

  return textElement;
};

/**
 * ReferencedEntries component displays entries that reference an asset
 */
const ReferencedEntries: React.FC<ReferencedEntriesProps> = ({
  entries = [],
  localeData = [],
  onEntryClick,
  maxVisibleEntries = 4,
  testId = 'cs-referenced-entries',
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const popoverAnchorRef = useRef<HTMLDivElement>(null);

  // Filter entries based on search text
  const filteredEntries = useMemo(() => {
    if (!searchText.trim()) return entries;
    const searchLower = searchText.toLowerCase().trim();
    return entries.filter(
      (entry) =>
        entry.title?.toLowerCase().includes(searchLower) ||
        entry.content_type_title?.toLowerCase().includes(searchLower)
    );
  }, [entries, searchText]);

  // Entries to show in the main view (limited to maxVisibleEntries)
  const visibleEntries = entries.slice(0, maxVisibleEntries);
  const hasMoreEntries = entries.length > maxVisibleEntries;

  const handleEntryClick = (entry: ReferenceEntry) => {
    if (onEntryClick) {
      onEntryClick(entry);
    }
  };

  const handleViewAllClick = () => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
    setSearchText('');
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const handleSearchClear = () => {
    setSearchText('');
  };

  // Don't render if no entries
  if (!entries || entries.length === 0) {
    return null;
  }

  const renderEntryItem = (entry: ReferenceEntry, index: number, isPopover: boolean = false) => {
    const localeName = getLocaleName(entry.locale, localeData);
    const entryTitle = entry.title || 'Untitled';
    const metaText = `${localeName} | ${entry.content_type_title}`;

    return (
      <div
        key={`${entry.entry_uid}-${index}`}
        className="referenced-entries__item"
        data-test-id={`${testId}-item-${index}`}
      >
        <div className="referenced-entries__item-content">
          <TruncatedText
            text={entryTitle}
            className="referenced-entries__item-title"
            onClick={() => {
              handleEntryClick(entry);
              if (isPopover) {
                handlePopoverClose();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEntryClick(entry);
                if (isPopover) {
                  handlePopoverClose();
                }
              }
            }}
            role="button"
            tabIndex={0}
            testId={`${testId}-item-title-${index}`}
          />
          <TruncatedText
            text={metaText}
            className="referenced-entries__item-meta"
            testId={`${testId}-item-meta-${index}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="referenced-entries" data-test-id={testId}>
      <div className="referenced-entries__header">
        <FieldLabel
          version="v2"
          htmlFor="referenced-entries"
          testId={`${testId}-label`}
        >
          <span className="referenced-entries__header-label">
            Referenced In Entries
          </span>
        </FieldLabel>
      </div>

      <div className="referenced-entries__list">
        {visibleEntries.map((entry, index) => renderEntryItem(entry, index, false))}
      </div>

      {hasMoreEntries && (
        <div className="referenced-entries__view-all-wrapper" ref={popoverAnchorRef}>
          <Button
            buttonType="tertiary"
            size="small"
            version="v2"
            onClick={handleViewAllClick}
            testId={`${testId}-view-all-button`}
            className="referenced-entries__view-all-button"
          >
            View All ({entries.length})
          </Button>
        </div>
      )}

      <Popover
        isOpen={isPopoverOpen}
        onClose={handlePopoverClose}
        anchorRef={popoverAnchorRef}
        boundaryRef={popoverAnchorRef}
        popoverWidth={320}
        offset={8}
        dismissOnClickOutside={true}
        testId={`${testId}-popover`}
      >
        <div className="referenced-entries__popover">
          <div className="referenced-entries__popover-header">
            <div className="referenced-entries__popover-header-left">
              <span className="referenced-entries__popover-title">
                Referenced In Entries
              </span>
              <div className="referenced-entries__popover-count-badge">
                {entries.length}
              </div>
            </div>
            <Button
              buttonType="tertiary"
              size="small"
              version="v2"
              onlyIcon={true}
              onClick={handlePopoverClose}
              testId={`${testId}-popover-close`}
              className="referenced-entries__popover-close-button"
            >
              <Icon icon="Cancel" version="v2" size="small" />
            </Button>
          </div>

          <div className="referenced-entries__popover-search">
            <Search
              placeholder="Search entry"
              width="full"
              type="secondary"
              version="v2"
              size="small"
              value={searchText}
              onChange={(e: any) => handleSearchChange(e.target?.value || e)}
              onClear={handleSearchClear}
              testId={`${testId}-popover-search`}
            />
          </div>

          <div className="referenced-entries__popover-list">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((entry, index) =>
                renderEntryItem(entry, index, true)
              )
            ) : (
              <div
                className="referenced-entries__popover-empty"
                data-test-id={`${testId}-popover-empty`}
              >
                No entries found
              </div>
            )}
          </div>

          <div className="referenced-entries__popover-footer">
            <Button
              buttonType="tertiary"
              size="small"
              version="v2"
              onClick={handlePopoverClose}
              testId={`${testId}-popover-cancel`}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default ReferencedEntries;

ReferencedEntries.displayName = 'ReferencedEntries';
